import config from "./config.json";
import { compileTemplate, handleGraphqlQueryFilter, handleRESTQueryFilter } from "./utils";
import { parse } from "graphql";
import _ from 'lodash';
import { Response } from 'miragejs'

const { seeders, origins, graphqlSeeders, passthroughURLs, timing } = compileTemplate(config);
const stubResponses = {}
const stubGraphqlResponses = {}

const loadSeeders = () => {
    try {
        // Load the seeders from the configuration file
        for (const key of Object.keys(seeders)) {
            const stub = seeders[key]
            if (stub) {
                const response = require('./' + stub.response);
                stubResponses[key] = compileTemplate(response);
            }
        }
        console.log('seeding')
        // Load the graphqlSeeders from the configuration file
        for (const key of Object.keys(graphqlSeeders)) {
            const stub = graphqlSeeders[key]
            if (stub) {
                const response = require('./' + stub.response);
                stubGraphqlResponses[key] = response;
            }
        }
    } catch (e) {
        console.error('Error loading seeders:', e);
    }
}

loadSeeders();

/** 
 * Seeds the Mirage server with predefined routes and responses.
 * This function sets up the routes based on the seeders defined in the configuration.
 * It also configures passthrough URLs to allow certain requests to bypass Mirage.
 * @param {Object} route - The Mirage route object to configure.
 * @returns {void}
 */
const seed = async (route) => {
    // Set the response timing for the routes based on the configuration
    route.timing = timing;
    route.logging = true;
    // Seed the Mirage server with stub data
    route.urlPrefix = process.env.REACT_APP_OCCM_ENDPOINT

    /**
     * Conditional passthrough for requests.
     * This allows requests to bypass Mirage if they are not meant to be handled by it.
     * It checks if the request has a specific header (`x-offline-mode`) set to true.
     * If the header is not present or set to false, the request will be processed by Mirage.
     * This is useful for allowing certain requests to go through to the actual server
     * while still using Mirage for other requests.
     * @param {Object} req - The request object.
     * @returns {boolean} - Returns true if the request should be passed through, false otherwise.
     */
    route.passthrough("*", (req) => {
        // Defaults to passthrough
        let passthrough = true;

        // If the request is for an arraybuffer, clear the responseType
        if (req.responseType === 'arraybuffer') {
            req.responseType = undefined;
        }
        // If the url is part of origins, let it not pass
        origins.forEach((url) => {
            if (req.url.startsWith(url)) {
                passthrough = false
            }
        })
        return passthrough;
    });
    /** 
     * Iterate over the seeders and set up the routes in Mirage.
     * Each seeder defines a type (GET, POST, etc.) and a URL,
     * along with the response that should be returned when that URL is hit.
     */
    for (const key of Object.keys(seeders)) {
        const stub = seeders[key]
        if (stub && stub.disabled !== true) {

            route[stub.type.toLowerCase()](stub.url, (schema, req) => {
                if (stub.filters) {
                    const stubWithQuery = handleRESTQueryFilter(stub, req);
                    if (stubWithQuery) {
                        return compileTemplate(stubWithQuery);
                    } else {
                        return stubResponses[key];
                    }
                }
                return stubResponses[key]
            });
        }
    }

    route.post("https://ec2.us-east-1.amazonaws.com/", (_schema, req) => {
        let xmlResponse = ''
        if (req.requestBody.startsWith('Action=DescribeVpcs')) {

            xmlResponse = require('./data/platform/ec2-vpcs.js').default;
        }
        if (req.requestBody.startsWith('Action=DescribeSubnets')) {

            xmlResponse = require('./data/platform/ec2-subnets.js').default;
        }
        if (req.requestBody.startsWith('Action=DescribeKeyPairs')) {

            xmlResponse = require('./data/platform/ec2-keypairs.js').default;
        }
        if (req.requestBody.startsWith('Action=DescribeInstanceStatus')) {

            xmlResponse = require('./data/platform/ec2-status.js').default;
        }
        const res = new Response(200, { "Vary": "accept-encoding", "Content-Type": "text/xml; charset=utf-8", "Transfer-Encoding": "chunked" }, xmlResponse);
        return res
    });

    route.post("https://cloudformation.us-east-1.amazonaws.com/", (_schema, req) => {
        let xmlResponse = ''
        if (req.requestBody.startsWith('Action=CreateStack')) {

            xmlResponse = require('./data/platform/cf-stack.js').default;
        }
        if (req.requestBody.startsWith('Action=DescribeStacks')) {

            xmlResponse = require('./data/platform/cf-status.js').default;
        }
        if (req.requestBody.startsWith('Action=DescribeStackEvents')) {

            xmlResponse = require('./data/platform/cf-events.js').default;
        }

        const res = new Response(200, { "Content-Type": "text/xml" }, xmlResponse);
        return res
    });

    route.post("https://iam.amazonaws.com/", (_schema, req) => {
        const xmlResponse = require('./data/platform/aws-iam.js').default;
        const res = new Response(200, { "Content-Type": "text/xml" }, xmlResponse);
        return res
    });


    route.post('/v1/services/ui-controller/graphql', (_schema, request) => {
        const requestBody = JSON.parse(request?.requestBody);
        const parsedQuery = parse(requestBody?.query);
        let operationName = parsedQuery?.definitions[0]?.name.value;
        // convert your query name or filter name or json file name to kebabCase wherever required
        operationName = _.kebabCase(operationName);

        // add query name here if for different filter names if you need different set of data.
        // if this is the case then append filter name to query name inside handleGraphqlQueryFilter by adding an entry
        if (operationName === "user-preferences") {
            const response = handleGraphqlQueryFilter(parsedQuery, operationName, stubGraphqlResponses);
            return response;
        }

        return stubGraphqlResponses[operationName];
    });

    /**
     * Set up passthrough URLs in Mirage.
     * This allows certain requests to bypass Mirage and go directly to the server.
     */
    route.passthrough(...passthroughURLs);
}

export { seed }